import React, { useState, useEffect } from 'react';
import { invitePeers, sendReminders, getComparison, getCurrentUser } from '../services/api';
import '../styles/main.css';

const InvitePeersModal = ({ isOpen, onClose }) => {
  const [emails, setEmails] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen]);

  const loadStats = async () => {
    try {
      const user = getCurrentUser();
      if (user && user.id) {
        const data = await getComparison(user.id);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleAddEmail = () => {
    const newEmails = emails
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e && validateEmail(e));

    if (newEmails.length > 0) {
      setEmailList([...emailList, ...newEmails]);
      setEmails('');
      setError('');
    } else {
      setError('Please enter valid email addresses');
    }
  };

  const handleRemoveEmail = (index) => {
    setEmailList(emailList.filter((_, i) => i !== index));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        // Parse CSV - assuming simple format with one email per line or comma-separated
        const parsedEmails = text
          .split(/[\n,]/)
          .map(e => e.trim())
          .filter(e => e && validateEmail(e));

        if (parsedEmails.length > 0) {
          setEmailList([...emailList, ...parsedEmails]);
          setSuccess(`Added ${parsedEmails.length} email(s) from file`);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('No valid emails found in file');
        }
      } catch (err) {
        setError('Failed to parse file');
      }
    };
    reader.readAsText(file);
  };

  const handleSendInvites = async () => {
    if (emailList.length === 0) {
      setError('Please add at least one email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await invitePeers(emailList);
      setSuccess(`Sent ${response.invitations.length} invitation(s) successfully!`);
      setEmailList([]);
      await loadStats(); // Reload stats
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await sendReminders();
      const sentCount = response.reminders.filter(r => r.sent).length;
      setSuccess(`Sent ${sentCount} reminder(s) successfully!`);
      await loadStats(); // Reload stats
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to send reminders');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invite-peers-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Compare Your Footprint with Others</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {stats && (
            <div className="stats-container">
              <p className="stats-text">
                <strong>Response Status:</strong> {stats.completed} of {stats.totalInvited} invited have responded
              </p>
            </div>
          )}

          <div className="email-input-section">
            <label htmlFor="emails">Enter Email Addresses</label>
            <textarea
              id="emails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="Enter email addresses separated by commas, semicolons, or new lines&#10;example@domain.com, another@domain.com"
              rows="4"
              disabled={loading}
            />
            <div className="button-row">
              <button 
                className="secondary-button"
                onClick={handleAddEmail}
                disabled={loading || !emails.trim()}
              >
                Add to List
              </button>
              <button 
                className="secondary-button"
                onClick={() => setShowFileUpload(!showFileUpload)}
                disabled={loading}
              >
                {showFileUpload ? 'Hide' : 'Upload CSV'}
              </button>
            </div>

            {showFileUpload && (
              <div className="file-upload-section">
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                <p className="help-text">Upload a CSV or TXT file with one email per line</p>
              </div>
            )}
          </div>

          {emailList.length > 0 && (
            <div className="email-list-section">
              <h3>Email List ({emailList.length})</h3>
              <div className="email-list">
                {emailList.map((email, index) => (
                  <div key={index} className="email-chip">
                    <span>{email}</span>
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveEmail(index)}
                      disabled={loading}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>

        <div className="modal-footer">
          <button 
            className="primary-button"
            onClick={handleSendInvites}
            disabled={loading || emailList.length === 0}
          >
            {loading ? 'Sending...' : `Send ${emailList.length} Invitation${emailList.length !== 1 ? 's' : ''}`}
          </button>
          
          {stats && stats.totalInvited > stats.completed && (
            <button 
              className="secondary-button"
              onClick={handleSendReminders}
              disabled={loading}
            >
              Send Reminders to Non-Responders
            </button>
          )}
          
          <button 
            className="secondary-button"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitePeersModal;
